import React, { createContext, useContext, useEffect, useState } from 'react';

// Create context for Square
const SquareContext = createContext(null);

// Custom hook to use Square context
export const useSquare = () => {
  const context = useContext(SquareContext);
  if (!context) {
    throw new Error('useSquare must be used within a SquareProvider');
  }
  return context;
};

const SquareProvider = ({ children }) => {
  const [payments, setPayments] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSquareSDK = async () => {
      try {
        // Check if Square SDK is already loaded
        if (window.Square) {
          console.log('✅ Square SDK already loaded');
          await initializePayments();
          return;
        }

        // Dynamically load Square Web Payments SDK
        const script = document.createElement('script');
        // Use production or sandbox SDK based on environment
        const environment = process.env.REACT_APP_SQUARE_ENVIRONMENT || 'sandbox';
        const sdkUrl = environment === 'production'
          ? 'https://web.squarecdn.com/v1/square.js'
          : 'https://sandbox.web.squarecdn.com/v1/square.js';
        script.src = sdkUrl;
        script.type = 'text/javascript';
        script.async = true;
        console.log(`🔧 Loading Square SDK from ${environment} environment:`, sdkUrl);

        script.onload = async () => {
          console.log('✅ Square SDK loaded successfully');
          await initializePayments();
        };

        script.onerror = () => {
          const errorMsg = 'Failed to load Square Web Payments SDK';
          console.error('❌', errorMsg);
          setError(errorMsg);
          setIsLoaded(false);
        };

        document.head.appendChild(script);
      } catch (err) {
        console.error('❌ Error loading Square SDK:', err);
        setError(err.message);
        setIsLoaded(false);
      }
    };

    const initializePayments = async () => {
      try {
        const applicationId = process.env.REACT_APP_SQUARE_APPLICATION_ID;
        const locationId = process.env.REACT_APP_SQUARE_LOCATION_ID;

        if (!applicationId || !locationId) {
          throw new Error(
            'Missing Square credentials. Please set REACT_APP_SQUARE_APPLICATION_ID and REACT_APP_SQUARE_LOCATION_ID in your .env file'
          );
        }

        console.log('🔧 Initializing Square Payments with Application ID:', applicationId);
        console.log('📍 Location ID:', locationId);

        // Initialize the Square Payments object
        const paymentsInstance = window.Square.payments(applicationId, locationId);

        setPayments(paymentsInstance);
        setIsLoaded(true);
        console.log('✅ Square Payments initialized successfully');
      } catch (err) {
        console.error('❌ Error initializing Square Payments:', err);
        setError(err.message);
        setIsLoaded(false);
      }
    };

    loadSquareSDK();
  }, []);

  const contextValue = {
    payments,
    isLoaded,
    error
  };

  return (
    <SquareContext.Provider value={contextValue}>
      {children}
    </SquareContext.Provider>
  );
};

export default SquareProvider;
